import { RootState } from '@/store';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Select, Spin, Table, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthProvider';
import { setRequestFile, updateField } from '../../../../contextRedux/RequestFile/RequestFileModel';
import { setRequestFileStatuses } from '../../../../contextRedux/RequestFileStatus/RequestFileStatusStore';
import RequestFileService from '../../../../services/requestFile/RequestFileService';
import { Column } from '../../../../utils/column';
import './EditRequestFile.css';

const { Title } = Typography;
const { Option } = Select;

const carOptions: { value: string; label: string }[] = [
  { value: 'BH-06-HLN', label: 'FIAT DUCATO' },
  { value: 'BH-43-HEL', label: 'MERCEDES CIDAN' },
  { value: 'KLR-289', label: 'Scoda Octavia' },
  { value: 'BH-74-HLN', label: 'VW PASAT' },
  { value: 'BH-16-RDR', label: 'VW GOLF 4' },
  { value: 'BH-90-RMI', label: 'BMW' },

  { value: 'B-201-HLN', label: 'DACIA JOGGER' },
  { value: 'B-202-HLN', label: 'DACIA JOGGER' },
  { value: 'B-203-HLN', label: 'DACIA JOGGER' },
  { value: 'B-204-HLN', label: 'DACIA JOGGER' },
  { value: 'B-205-HLN', label: 'DACIA JOGGER' },

  { value: 'B-125-HLN', label: 'DACIA LOGAN' },
  { value: 'B-140-HLN', label: 'DACIA LOGAN' },
  { value: 'B-103-HLN', label: 'RENAULT ARKANA' },

  // { value: 'BH-28-RAR', label: 'DACIA LOGAN DIESEL' },
  // { value: 'BH-81-RAR', label: 'VW TOURAN' },
  // { value: 'BH-40-HEL', label: 'DACIA LOGAN ALBASTRU' },
  // { value: 'BH-01-HLN', label: 'DACIA STEI' },
  // { value: 'BH-10-HEL', label: 'DACIA LOGAN GRI' },
  // { value: 'BH-99-DYP', label: 'AUDI 80' },
  // { value: 'BH-46-HEL', label: 'VW CADDY' },
  // { value: 'BH-22-HLS', label: 'SEAT LEON' },
  // { value: 'BH-26-RVR', label: 'BMW' },
  // { value: 'BH-123-DAN', label: 'BMW x212' },
  // { value: 'B-97-GUC', label: 'WOLKSWAGEN CAROL' },
  // { value: 'B-97-GUB', label: 'WOLKSWAGEN CATA' },
  // { value: 'BH-14-DXX', label: 'LOGAN BENZINA' },
  // { value: 'B-97-GUC', label: 'WOLKSWAGEN CAROL' },
  // { value: 'B-97-GUB', label: 'WOLKSWAGEN CATA' },
  // { value: 'BH-09-LOG', label: 'DACIA LOGAN GRI' },
  // { value: 'BH-22-HLS', label: 'SEAT LEON' },
  // { value: 'BH-26-RVR', label: 'BMW' },
  // { value: 'BH-123-DAN', label: 'bmw x212' },
  // { value: 'BH999DY', label: 'Audi 80' },
  // { value: 'BH-16-RDR', label: 'VW GOLF 4' },
];

const EditRequestFile: React.FC = () => {
  const query = useLocation().search;
  const idFromUrl = new URLSearchParams(query).get('id');
  const dispatch = useDispatch();
  const { profile } = useAuth();
  const { message } = App.useApp();

  const [id, setId] = useState<string | null>(idFromUrl);
  const reduxRequestFile = useSelector((state: RootState) => state?.models?.requestFile);
  const statuses = useSelector((state: RootState) => state?.store?.requestFileStatus?.data);

  useEffect(() => {
    setId(idFromUrl);
  }, [idFromUrl]);

  useEffect(() => {
    dispatch(updateField({ field: 'id', value: id }));
  }, [id, dispatch]);

  const {
    data: _data,
    isFetching: isRequestFileLoading,
    refetch: search,
  } = useQuery({
    queryKey: ['requestFiles'],
    queryFn: () =>
      RequestFileService.search({
        ids: [id!],
        isContractIncluded: true,
        isWorkPlaceIncluded: true,
        isCompanyIncluded: true,
        isAgentIncluded: true,
        isStatusIncluded: true,
        isRequestFileSystemTypesIncluded: true,
        isAssignedTechnicianIncluded: true,
        name: '',
        termen: '',
        take: 10,
        skip: 0,
        isActive: true,
      }).then((data) => {
        if (data?.response && data?.response.length > 0) {
          dispatch(setRequestFile(data?.response[0]));
        }
        return data?.response[0];
      }),
    enabled: Boolean(id),
  });

  const { data: _status, isFetching: isStatusLoading } = useQuery({
    queryKey: ['requestFileStatuses'],
    queryFn: () =>
      RequestFileService.getStatuses().then((data) => {
        dispatch(setRequestFileStatuses(data?.response));

        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}-${date.getFullYear()}`;
  }, []);

  const getCoordinatesFromOpenCage = useCallback(async (address: any) => {
    try {
      const url = `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(
        address
      )}&key=2f8e018dc8ed4c96b19abf22829564a0`;

      // Make the API call
      const response = await fetch(url);

      // Check for a successful response
      if (!response.ok) {
        throw new Error('Failed to fetch coordinates');
      }

      const data = await response.json();

      // Check if results are available
      if (data?.results?.length > 0) {
        const location = data.results[0]?.geometry;
        return {
          latitude: location?.lat,
          longitude: location?.lng,
        };
      } else {
        console.error('Location not found.');
        return null;
      }
    } catch (error) {
      console.error('Error fetching coordinates from OpenCage:', error);
      return null;
    }
  }, []);

  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            resolve({ latitude, longitude });
          },
          (error) => {
            reject(error);
          }
        );
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  }, []);

  const handleGoogleMaps = useCallback(async (address: any) => {
    try {
      // Get current location coordinates
      const currentCoordinates: any = await getCurrentLocation();

      // Get destination coordinates
      const destinationCoordinates = await getCoordinatesFromOpenCage(address);

      if (!currentCoordinates || !destinationCoordinates) {
        console.error('Invalid coordinates:', { currentCoordinates, destinationCoordinates });
        return;
      }

      if (destinationCoordinates) {
        const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${currentCoordinates.latitude},${currentCoordinates?.longitude}&destination=${destinationCoordinates.latitude},${destinationCoordinates.longitude}`;

        // Ensure window.open() is executed in response to user interaction
        window.open(googleMapsUrl, '_blank');
      } else {
        console.error('Could not get coordinates for the destination.');
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    }
  }, []);

  const handleWaze = useCallback(async (address: any) => {
    try {
      // Get current location coordinates
      const currentCoordinates: any = await getCurrentLocation();

      // Get destination coordinates
      const destinationCoordinates = await getCoordinatesFromOpenCage(address);

      if (destinationCoordinates) {
        const wazeUrl = `https://www.waze.com/ro/live-map/directions?from=ll.${currentCoordinates.latitude},${currentCoordinates.longitude}&to=ll.${destinationCoordinates.latitude},${destinationCoordinates.longitude}`;
        window.open(wazeUrl, '_blank');
      } else {
        console.error('Could not get coordinates for the destination.');
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    }
  }, []);

  // const getTime = useCallback(async (record: any) => {
  //   const workPlaceAddress = record.workPlace.address;

  //   const officeAddress = 'Strada Tiglarilor, nr 17, Oradea';

  //   return new Promise((resolve, reject) => {
  //     if (navigator.geolocation) {
  //       navigator.geolocation.getCurrentPosition(
  //         async (_position) => {
  //           //const { latitude, longitude } = position.coords;

  //           const url = `https://api.distancematrix.ai/maps/api/distancematrix/json?origins=${workPlaceAddress}&destinations=${officeAddress}&key=wECLI7IaWQU2rnFTdmd80vaHxzp9q1eCjasbJAq6CwZHQ38e7VxLjjAHrmWCLGq5`;

  //           const headers = {
  //             Accept: 'application/json, charset=utf-8',
  //             'Content-Type': 'application/json',
  //           };

  //           try {
  //             const response = await fetch(url, {
  //               method: 'GET', // Change to GET because Distance Matrix API uses GET
  //               headers: headers,
  //             });

  //             if (!response.ok) {
  //               throw new Error(`HTTP error! Status: ${response.status}`);
  //             }

  //             const data = await response.json();

  //             // Ensure we return the data if it exists, else handle the error
  //             if (data?.rows && data?.rows[0]) {
  //               resolve(data); // Return the fetched data
  //             } else {
  //               reject('Unable to retrieve route information.');
  //             }
  //           } catch (error) {
  //             reject(`Error fetching route data: ${error}`);
  //           }
  //         },
  //         (error) => {
  //           reject(`Error with geolocation: ${error.message}`);
  //         }
  //       );
  //     } else {
  //       reject('Geolocation is not supported by this browser.');
  //     }
  //   });
  // }, []);

  const { mutate: requestFileMutate, isPending: isRequestFileMutating } = useMutation({
    mutationFn: (formData: any) => RequestFileService.upsert(formData.id, formData),
    onSuccess: async (responseData: any, _variables: any) => {
      if (responseData?.errors?.length <= 0) {
        await message.success(`Solicitarea ${responseData?.response?.number} a fost salvata cu succes`);
        search();
      } else {
        responseData?.errors.forEach((error: any) => {
          message.error(error);
        });
      }
    },
    onError: (error: any) => {
      console.error('Error processing your request:', error);
      message.error('There was an error saving the request.');
    },
  });

  const handleRequestFile = useCallback(
    async (data: any) => {
      // Clone the data object
      const mutableData = { ...data };
      const { newStatusId } = handleStatusAndButton(mutableData.statusId);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;

            if (mutableData.statusId === 1) {
              mutableData.startingLatitude = latitude;
              mutableData.startingLongitude = longitude;
            }
            if (mutableData.statusId === 2) {
              mutableData.arrivalingLatitude = latitude;
              mutableData.arrivalingLongitude = longitude;

              // Wait for the travel time and distance calculation
              try {
                // const transportDetails: any = await getTime(mutableData);
                // // Extract the details you need and assign them to mutableData
                // mutableData.elapsedTime = transportDetails.rows[0].elements[0].duration.value / 60; // Convert seconds to minutes
                // mutableData.traveledKm = transportDetails.rows[0].elements[0].distance.value / 1000; // Convert meters to kilometers
              } catch (error) {
                message.error('Error getting transport details: ' + error);
              }
            }
            if (mutableData.statusId === 3) {
              mutableData.interventionStart = new Date().toISOString();
            }
            if (mutableData.statusId === 4) {
              mutableData.interventionEnd = new Date().toISOString();

              window.open(`/service-maintenance/interventionFile/new?requestFileId=${id}`, '_blank');
            }

            const formData = {
              ...mutableData,
              statusId: newStatusId,
              assignedTechnicianId: profile?.id,
            };
            requestFileMutate(formData);
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              message.error('Location access denied. Button has been disabled.');
            } else {
              message.error('Error getting location: ' + error.message);
            }
          }
        );
      } else {
        message.error('Geolocation is not supported by this browser.');
      }
    },
    [profile]
  );

  const handleStatusAndButton = useCallback((currentStatusId: number) => {
    let newStatusId, buttonText;

    switch (currentStatusId) {
      case 1:
        newStatusId = 2;
        buttonText = 'Merg eu';
        break;
      case 2:
        newStatusId = 3;
        buttonText = 'Am ajuns';
        break;
      case 3:
        newStatusId = 4;
        buttonText = 'Start interventie';
        break;
      case 4:
        newStatusId = 4;
        buttonText = 'Finalizata';
        break;
      default:
        newStatusId = currentStatusId;
        buttonText = '';
    }

    return { newStatusId, buttonText };
  }, []);

  const initialData: any[] = useMemo(
    () => [
      {
        key: 'number',
        title: 'Number',
        dataIndex: 'number',
        render: (_number: any) => <strong>{reduxRequestFile?.number}</strong>,
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        render: (_record: any) => (
          <Select value={reduxRequestFile?.statusId} disabled>
            {statuses?.map((status) => (
              <Option key={status.id} value={status.id}>
                {status.name}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        key: 'isUrgent',
        dataIndex: 'isUrgent',
        title: 'Urgenta',
        render: (_record: any) => <strong>{reduxRequestFile?.isUrgent ? 'Da' : 'Nu'}</strong>,
      },
      {
        key: 'postBy',
        title: 'Postata de',
        dataIndex: 'postBy',
        render: (_record: any) => <strong>{reduxRequestFile?.postBy ?? ''}</strong>,
      },
      {
        key: 'interventionDate',
        title: 'Data programata',
        dataIndex: 'interventionDate',
        render: (_record: any) => <strong>{formatDate(reduxRequestFile?.interventionDate || '')}</strong>,
      },
      {
        key: 'contract',
        title: 'Contract',
        dataIndex: 'contract',
        render: (_record: any) => <strong>{reduxRequestFile?.contract?.number}</strong>,
      },
      {
        key: 'requestFileSystemTypes',
        title: 'Tip de sistem',
        dataIndex: 'requestFileSystemTypes',
        render: (_requestFileSystemTypes: any[]) => (
          <strong>
            {reduxRequestFile?.requestFileSystemTypes && reduxRequestFile?.requestFileSystemTypes?.length > 0 && (
              <span>
                {reduxRequestFile?.requestFileSystemTypes?.length > 0
                  ? reduxRequestFile?.requestFileSystemTypes
                      ?.map((type: any) => `[${type?.systemType?.name}]`)
                      .join(' ')
                  : ''}
              </span>
            )}
          </strong>
        ),
      },
      {
        key: 'company',
        title: 'Client',
        render: (record: any) => {
          const company = record?.metadata?.company ?? record?.company;
          return <div>{company?.name}</div>;
        },
      },
      {
        key: 'workPlace',
        title: 'Puncte de lucru',
        dataIndex: '',
        render: (record: any) => {
          return (
            <strong>
              {record?.metadata?.workPlace?.name ?? record?.workPlace?.name ?? reduxRequestFile?.workPlace?.name}
            </strong>
          );
        },
      },
      {
        key: 'workPlaceAddress',
        dataIndex: '',
        title: 'Adresa',
        render: (record: any) => (
          <>
            <div>
              <strong>
                {record?.metadata?.workPlace.address ??
                  record?.workPlace?.address ??
                  reduxRequestFile?.workPlace?.address}
              </strong>
            </div>
            <div>
              <Button
                style={{ display: 'contents' }}
                onClick={() =>
                  handleGoogleMaps(
                    record?.metadata?.workPlace.address ??
                      record?.workPlace?.address ??
                      reduxRequestFile?.workPlace?.address
                  )
                }
              >
                <img
                  src='https://lh3.googleusercontent.com/9tLfTpdILdHDAvGrRm7GdbjWdpbWSMOa0csoQ8pUba9tLP8tq7M4Quks1xuMQAVnAxVfryiDXRzZ-KDnkPv8Sm4g_YFom1ltQHjQ6Q'
                  alt='GoogleMaps'
                  style={{
                    height: '30px',
                  }}
                />
              </Button>
              <Button
                style={{ display: 'contents' }}
                onClick={() =>
                  handleWaze(
                    record?.metadata?.workPlace.address ??
                      record?.workPlace?.address ??
                      reduxRequestFile?.workPlace?.address
                  )
                }
              >
                <img
                  src='https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/cd/87/6d/cd876daf-4c14-2ca0-05d7-6586ed8b6833/AppIcon-0-0-1x_U007emarketing-0-7-0-85-220.png/230x0w.webp'
                  alt='Waze'
                  style={{
                    height: '30px',
                    borderRadius: 10,
                  }}
                />
              </Button>
            </div>
          </>
        ),
      },
      {
        key: 'locationInfo',
        title: 'Informatii locatie',
        dataIndex: 'locationInfo',
        render: (_record: any) => <strong>{reduxRequestFile?.locationInfo}</strong>,
      },
      {
        key: 'registrationDate',
        title: 'Data inregistrare',
        dataIndex: 'interventionDate',
        render: (_record: any) => <strong>{formatDate(reduxRequestFile?.interventionDate || '')}</strong>,
      },
      {
        key: 'sla',
        dataIndex: 'SLA',
        title: 'SLA',
        render: (_record: any) => <strong>60 min</strong>,
      },
      {
        key: 'slaUrgent',
        dataIndex: 'SLA URGENT',
        title: 'SLA URGENT',
        render: (_record: any) => <strong>360 min</strong>,
      },
      {
        key: 'agentName',
        title: 'Persoana solicitanta',
        dataIndex: 'agent',
        render: (_record: any) => <strong>{reduxRequestFile?.agent?.name}</strong>,
      },
      {
        key: 'agentPhone',
        title: 'Numar de telefon persoana solicitanta',
        dataIndex: 'agent',
        render: (_record: any) => (
          <strong>{reduxRequestFile?.agent?.phone != null ? reduxRequestFile?.agent?.phone[0] : ''}</strong>
        ),
      },
      {
        key: 'remarks',
        title: 'Descriere solicitare',
        dataIndex: 'remarks',
        render: (_record: any) => <strong>{reduxRequestFile?.remarks}</strong>,
      },
      {
        key: 'assignedTechnician',
        title: 'Tehnician asignat',
        dataIndex: 'assignedTechnician',
        render: (_record: any) => <strong>{reduxRequestFile?.assignedTechnician?.name}</strong>,
      },
      {
        key: 'locationHistory',
        dataIndex: 'Istoric locatie',
        title: 'Istoric locatie',
        render: (_record: any) => <Button>Afiseaza istoric locatie</Button>,
      },
      {
        key: 'vehicle',
        dataIndex: 'vehicle',
        title: 'Autoturism',
        render: (_record: any) => (
          <>
            {reduxRequestFile?.vehicle ? (
              <strong>{reduxRequestFile?.vehicle}</strong>
            ) : (
              <Select
                style={{ width: '100%' }}
                placeholder='Selectati un autoturism'
                onChange={(e: any) => dispatch(updateField({ field: 'vehicle', value: e }))}
                value={reduxRequestFile?.vehicle}
              >
                {carOptions.map((car) => (
                  <Option key={car.value} value={car.value}>
                    {car.value} {car.label}
                  </Option>
                ))}
              </Select>
            )}
          </>
        ),
      },
    ],
    [reduxRequestFile, statuses]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'title',
        key: 'title',
        render: (text) => <>{text}</>,
      },
      {
        title: '',
        dataIndex: 'value',
        key: 'value',
      },
    ],
    [reduxRequestFile, statuses]
  );

  const dataSource = initialData?.map((item) => ({
    key: item?.key,
    title: item?.title,
    value: item?.render(null, reduxRequestFile),
  }));

  // Render mobile view data item
  const renderDataItem = (title: string, value: React.ReactNode) => (
    <div className='data-item'>
      <div className='data-item-title'>{title}</div>
      <div className='data-item-value'>{value}</div>
    </div>
  );

  if (isRequestFileLoading || isStatusLoading || isRequestFileMutating) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <div className='responsive-container'>
      {/* Header Section */}
      <div className='header-section'>
        <Title level={2}>Informatii solicitare</Title>
        <Title level={3}>{reduxRequestFile?.company?.name}</Title>
        <Title level={4}>{reduxRequestFile?.workPlace?.name}</Title>
      </div>

      {/* Desktop Table View */}
      <div className='desktop-table'>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          showHeader={false}
          rowKey='key'
          scroll={{ x: 'max-content' }}
        />
      </div>

      {/* Mobile List View */}
      <div className='mobile-view'>
        <div className='data-container'>
          {initialData.map((item) => (
            <React.Fragment key={item.key}>
              {renderDataItem(item.title, item.render(null, reduxRequestFile))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Action Button */}
      <div className='action-button-container'>
        {reduxRequestFile?.statusId != 5 && reduxRequestFile?.vehicle && (
          <Button type='primary' className='action-btn' onClick={() => handleRequestFile(reduxRequestFile)}>
            {reduxRequestFile ? handleStatusAndButton(reduxRequestFile?.statusId).buttonText : 'Loading...'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default EditRequestFile;
